import React, { useState, useMemo } from 'react';
import HelpTooltip, { HELP } from './HelpTooltip';
import { Check, Plus, Search } from 'lucide-react';

/* ====== ブース選択コンポーネント（時間別入力で使用） ======
   value はブース「名」。新規名はそのまま onChange され、保存側で booths から id 解決 or 新規作成する。 */
export default function BoothPicker({ booths, value, onChange, required = false }) {
  const [query, setQuery] = useState('');
  const [showNewInput, setShowNewInput] = useState(false);
  // ブースは1日1つで、朝に選んだらほぼ触らない。選んだあとも一覧を出しっぱなしにすると
  // 時間別入力が200px以上下に押し下げられるので、選択後は畳んで「変更」で開く。
  const [open, setOpen] = useState(false);
  const showRequired = required && !((value || '').trim());
  const picked = !!(value || '').trim();
  const expanded = open || !picked;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return booths;
    return booths.filter(b => b.name.toLowerCase().includes(q));
  }, [booths, query]);

  const exactMatch = booths.some(b => b.name === query.trim());
  const hasQuery = query.trim().length > 0;
  const selectedExists = value && booths.some(b => b.name === value);

  return (
    <div>
      <label style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          ブース（その日の店舗）
          {/* 選び終わったあとまで「必須」を出し続けると、まだ足りないのかと迷わせる。 */}
          {showRequired && (
            <span style={{ fontSize: 'var(--t-note)', fontWeight: 800, color: 'var(--loss)', background: 'var(--loss-bg)', border: '1px solid #6B3A36', borderRadius: 6, padding: '1px 6px' }}>必須</span>
          )}
          <HelpTooltip title={HELP.booth.title} body={HELP.booth.body} />
        </span>
        {picked && (
          <span style={{ display: 'inline-flex', gap: 10 }}>
            <button type="button" onClick={() => setOpen((v) => !v)} style={{
              background: 'transparent', border: 'none', color: 'var(--mark)', fontSize: 'var(--t-note)', fontWeight: 700, cursor: 'pointer',
            }}>{expanded ? '閉じる' : '変更'}</button>
            <button type="button" onClick={() => { onChange(''); setQuery(''); setShowNewInput(false); setOpen(true); }} style={{
              background: 'transparent', border: 'none', color: 'var(--loss)', fontSize: 'var(--t-note)', cursor: 'pointer',
            }}>クリア</button>
          </span>
        )}
      </label>

      {/* 未選択の注意（必須時のみ） */}
      {showRequired && (
        <div style={{
          padding: '10px 12px', marginBottom: 10,
          background: 'var(--loss-bg)',
          border: '1px solid #6B3A36',
          borderRadius: 10, fontSize: 'var(--t-note)', color: 'var(--loss)', fontWeight: 600, lineHeight: 1.5,
        }}>
          先に本日のブースを選択してください。選択すると時間別入力ができます。
        </div>
      )}

      {/* 現在の選択 */}
      {value && (
        <div style={{
          padding: '12px 14px', marginBottom: 10,
          background: 'var(--mark-soft)',
          border: '1.5px solid #33406E',
          borderRadius: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Check size={16} color="var(--mark)" />
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--mark)', flex: 1 }}>{value}</span>
          {!selectedExists && (
            <span style={{ fontSize: 'var(--t-note)', padding: '2px 8px', background: 'var(--hold-bg)', color: 'var(--hold)', borderRadius: 6, fontWeight: 700 }}>新規</span>
          )}
        </div>
      )}

      {/* 検索ボックス */}
      {expanded && booths.length > 5 && (
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <Search size={14} color="var(--ink-mute)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          <input
            type="text" inputMode="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="ブース名で検索..."
            style={{
              width: '100%', padding: '10px 12px 10px 34px',
              background: 'var(--paper-sunk)',
              border: '1px solid var(--rule-strong)',
              borderRadius: 10, color: 'var(--ink)', fontSize: 14,
              boxSizing: 'border-box', outline: 'none',
              minHeight: 44,
            }}
          />
        </div>
      )}

      {/* ブース一覧（チップ） */}
      {expanded && filtered.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6,
          maxHeight: 180, overflowY: 'auto',
          padding: 4,
          background: 'var(--paper-sunk)',
          borderRadius: 10,
          marginBottom: 8,
        }}>
          {filtered.map(b => {
            const active = value === b.name;
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => { onChange(b.name); setShowNewInput(false); setOpen(false); }}
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  fontSize: 14, fontWeight: 600,
                  border: `1.5px solid ${active ? 'var(--mark)' : 'var(--ink-mute)'}`,
                  background: active ? 'var(--mark-soft)' : 'var(--paper-sunk)',
                  color: active ? 'var(--mark)' : 'var(--ink)',
                  cursor: 'pointer',
                  minHeight: 44,
                  transition: 'all 120ms',
                }}
              >
                {b.name}
              </button>
            );
          })}
        </div>
      )}

      {expanded && filtered.length === 0 && hasQuery && (
        <div style={{ padding: '12px', textAlign: 'center', fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>
          一致するブースがありません
        </div>
      )}

      {/* 新規ブース追加 */}
      {!expanded ? null : !showNewInput ? (
        <button
          type="button"
          onClick={() => setShowNewInput(true)}
          style={{
            width: '100%', padding: '12px',
            background: 'var(--gain-bg)',
            border: '1.5px dashed #1E5540',
            borderRadius: 10,
            color: 'var(--gain)', fontSize: 14, fontWeight: 700,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            minHeight: 44,
          }}
        >
          <Plus size={16} /> 新しいブースを追加
        </button>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="新規ブース名を入力"
            style={{
              flex: 1, padding: '12px 14px',
              background: 'var(--paper-sunk)',
              border: '1.5px solid #1E5540',
              borderRadius: 10, color: 'var(--ink)', fontSize: 15,
              boxSizing: 'border-box', outline: 'none',
              minHeight: 44,
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const name = query.trim();
                if (name) { onChange(name); setShowNewInput(false); setOpen(false); }
              }
            }}
          />
          <button
            type="button"
            disabled={!query.trim() || exactMatch}
            onClick={() => {
              const name = query.trim();
              if (name) { onChange(name); setShowNewInput(false); setOpen(false); }
            }}
            style={{
              padding: '12px 16px',
              background: query.trim() && !exactMatch ? 'var(--gain-solid)' : 'var(--rule)',
              border: 'none', borderRadius: 10,
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: query.trim() && !exactMatch ? 'pointer' : 'not-allowed',
              minHeight: 44,
            }}
          >
            追加
          </button>
          <button
            type="button"
            onClick={() => { setShowNewInput(false); setQuery(''); }}
            style={{
              padding: '12px',
              background: 'var(--paper-sunk)',
              border: 'none', borderRadius: 10,
              color: 'var(--ink-soft)', fontSize: 14,
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            キャンセル
          </button>
        </div>
      )}
    </div>
  );
}
